// ai/helpers/team-intent.ts
//
// Does this slide request ask for the company's PEOPLE?
//
// The library path already knows: `infer-slide-shape` returns
// contentKind "people" and use-prefill-slide researches the real roster off
// that. The content-first add-slide path has no such signal — the blueprint is
// written before any layout inference runs — so "Add a slide with the storydoc
// team" reached the model with nothing but the sentence, and the model
// answered with DEPARTMENTS: "Product Innovators", "Engineering Experts",
// "Customer Success Champions". Functions dressed up as people. Dawid: "should
// add a slide with the team members, now it gave me the teams within
// storydoc."
//
// Reading the request text is enough to decide whether to spend a web search
// on the roster, and it is the same shape as this directory's other intent
// helpers (combo-intent, chart-intent).
//
// Tuned to be quiet rather than eager. A false positive costs a research call
// and a roster the blueprint may not use; a false negative just leaves today's
// behaviour. So it matches the ways people ask for a roster, not every mention
// of the word "team" — "built for teams that ship weekly" is a product claim,
// and "add a slide about our team culture" is about the culture.

// The phrases that ask for a roster.
//
// This started out requiring a determiner — "our team", "the team",
// "storydoc's team" — and that was too strict by exactly one word: "Create a
// slide with storydoc team" and "Add a slide about storydoc team" both missed,
// because "storydoc" is not "the". People do not reliably put an article in
// front of a company name.
//
// So a SINGULAR "team" is enough on its own, and the NOT_A_ROSTER list below
// carries the weight of ruling out the other senses of the word. Singular is
// doing real work here: "built for teams that ship weekly" and "pricing for
// teams" are the product senses, and they are plural.
const TEAM_PATTERNS: RegExp[] = [
  /\bteam\b(?!s)/i,
  /\bteam\s+members?\b/i,
  /\b(?:our|the|my)\s+(?:[\w-]+\s+)?(?:people|staff|crew|founders?|leadership|leaders|executives?|management)\b/i,
  /\b\w+(?:'s|s')\s+(?:people|staff|founders?|leadership)\b/i,
  /\b(?:meet|introduc\w*)\s+(?:the|our)\s+(?:people|crew|founders?|leadership)\b/i,
  /\b(?:who\s+we\s+are|about\s+us\s+team)\b/i,
  // THE SAME LESSON AS ABOVE, WHICH WAS ONLY EVER APPLIED TO "team".
  //
  // The three patterns above still demand a determiner ("our founders") or a
  // possessive ("storydoc's founders"), so "add a slide with storydoc
  // founders" matched nothing at all -- while "storydoc team" matched fine.
  // No roster was researched, and the blueprint answered with three named
  // co-founders and their careers: two of the three names, and every job
  // history on the slide, were invented. Naming a plausible stranger as
  // somebody's co-founder is worse than naming a department.
  //
  // "founders" carries a roster on its own -- there is no other thing a
  // company's founders could be -- so it needs no determiner. The senses that
  // are NOT about the people are listed in NOT_A_ROSTER below.
  /\b(?:co-?)?founders?\b/i,
  // A company name in front of a roster noun, which is how people actually
  // write it when the deck is not about their own company.
  /\b[\w-]+\s+(?:leadership|executives?|leaders)\b/i,
];

// Phrases that use the same words for something else. Checked first, because
// "built for teams" and "team culture" are about the product and the culture,
// not about who works here.
const NOT_A_ROSTER: RegExp[] = [
  /\bteam\s+(?:culture|values|structure|size|productivity|collaboration|workflow|process|performance|spirit|building|sport)\b/i,
  /\bfor\s+teams?\b/i,
  /\bteams?\s+that\b/i,
  /\b(?:build|building|grow|growing|hir\w+|scal\w+)\s+(?:a\s+|our\s+|the\s+)?team\b/i,
  // The senses of "founder" and "leadership" that are not a list of people.
  // These carry the weight for the bare patterns above, exactly as the "team"
  // entries do for a bare "team".
  /\bthought\s+leadership\b/i,
  /\bleadership\s+(?:principles|skills|style|training|development|lessons|qualities|philosophy)\b/i,
  /\bfounders?\s+(?:agreement|equity|vesting|stock|shares|mode|market\s+fit)\b/i,
  /\bfounder-?led\b/i,
  /\bfounding\s+(?:story|principles|vision)\b/i,
];

/**
 * True when the request is asking for named individuals.
 */
export const isTeamRequest = (text: string): boolean => {
  const t = String(text || "");
  if (!t.trim()) return false;
  if (NOT_A_ROSTER.some((re) => re.test(t))) return false;
  return TEAM_PATTERNS.some((re) => re.test(t));
};

// Department words, not company names. "our sales team" is a roster request
// about a department of the deck's own company; "storydoc team" names a
// different company outright.
const DEPARTMENTS = new Set([
  "sales", "marketing", "engineering", "product", "design", "support",
  "success", "operations", "ops", "finance", "legal", "hr", "people",
  "executive", "leadership", "management", "founding", "core", "whole",
  "entire", "extended", "wider", "global", "local", "remote", "amazing",
  "great", "talented", "dedicated", "expert", "internal", "senior", "own",
]);

const DETERMINERS = new Set(["our", "the", "my", "this", "a", "an", "your"]);

/**
 * The company a team request names, when it names one.
 *
 * "add a slide about storydoc team" on a deck whose Story Brain says Shopify
 * is a request about STORYDOC. Researching the deck's brand there would put
 * Shopify's executives on the slide — real people, wrong company, which is the
 * exact failure `research-team` was written to avoid. It is worse than the
 * statement fallback, because it looks right.
 *
 * Returns null for "our team" / "the leadership team" / "our sales team" —
 * those are the deck's own company, which the Story Brain already names.
 */
export const teamCompanyFromRequest = (text: string): string | null => {
  const m =
    /\b([\w-]+)(?:'s|s')\s+(?:team|people|staff|founders?|leadership)\b/i.exec(
      text,
    ) ??
    /\b([\w-]+)\s+team\b(?!s)/i.exec(text) ??
    // "storydoc founders" names a company just as plainly as "storydoc's
    // founders" does, and without this the request fell through to the DECK's
    // brand -- so a Storydoc founders slide on an agency's deck would have
    // researched the agency and put the wrong company's people on it. Real
    // names, wrong company, which is the failure research-team exists for.
    /\b([\w-]+)\s+(?:founders?|leadership|executives?|staff)\b/i.exec(text);
  if (!m) return null;
  const word = m[1].trim();
  const lower = word.toLowerCase();
  if (DETERMINERS.has(lower) || DEPARTMENTS.has(lower)) return null;
  // A single letter or a number is never a company.
  if (word.length < 2 || /^\d+$/.test(word)) return null;
  return word;
};

/**
 * Did the request ask for the FOUNDERS specifically, rather than the team?
 *
 * "Add a slide with storydoc founders" against a four-card layout produced
 * four people: the three real founders and the Head of Sales, who was pulled
 * in to fill the empty slot. Nobody had asked for him, and a slide titled
 * "Meet the Founders" listing someone who is not one misrepresents both him
 * and the company.
 *
 * The layout resizes to the item count (see elastic-layout), so answering with
 * three is enough to get a three-card slide -- the padding only happened
 * because nothing said the roster should stop at the founders.
 */
export const wantsFoundersOnly = (text: string): boolean => {
  const t = String(text || "");
  if (!/\b(?:co-?)?founders?\b|\bfounding\s+team\b/i.test(t)) return false;
  // "the founders and the team" / "founders and everyone else" asks for both,
  // and trimming to the founders there would drop what was asked for.
  if (
    /\band\s+(?:the\s+|our\s+)?(?:team|staff|everyone|rest)\b|\b(?:whole|entire|wider|rest\s+of\s+the)\s+team\b|\bteam\s+members?\b/i.test(
      t,
    )
  ) {
    return false;
  }
  return true;
};
